import React, { useEffect, useMemo, useState } from 'react';
import { useIdentity, IdentityLevel } from '../lib/identity';
import { LENS_OPTIONS, RegionLens } from '../lib/socialPulse';
import {
  MintedMarket,
  FIELD_OUTCOME_ID,
  listMintedMarketsForViewer,
  subscribeMintActivity,
  subscribeVetoEvents,
  vouchForMarket,
  vetoMarket,
  vetoPhaseFor,
  vouchesRequiredFor,
} from '../lib/mintedMarkets';

// Mint Queue — §6 vouching surface + §6.1 escape valves.
//
// This panel is the dashboard for user-minted markets. It shows
// everything the viewer can meaningfully act on:
//   PENDING  — Citizen can Vouch (needs 2 vouches to promote to ACTIVE).
//   ACTIVE   — the viewer's own ACTIVE markets (others' aren't shown here;
//              they promote into the Global Lens grid downstream).
//   STALE    — escape-valve state. Sovereign Veto button while the window
//              is open; countdown to Auto-Refund otherwise.
//   VOIDED_BY_SOVEREIGN / AUTO_REFUNDED — terminal states, shown briefly.

interface Props {
  lens: RegionLens;
}

const LENS_ACCENT: Record<RegionLens, string> = {
  LOCAL:  '#7dd3fc',
  GLOBAL: '#fcd34d',
  ASIA:   '#f472b6',
  EUROPE: '#a78bfa',
};

export default function MintQueue({ lens }: Props): React.JSX.Element | null {
  const identity = useIdentity();
  const [tick, setTick] = useState(0);

  // Re-render on (a) mint store mutations and (b) a 1-second wall
  // clock so countdowns animate even when no state has flipped yet.
  useEffect(() => subscribeMintActivity(() => setTick(t => t + 1)), []);
  useEffect(() => {
    const h = window.setInterval(() => setTick(t => t + 1), 1000);
    return () => window.clearInterval(h);
  }, []);

  // v2.2 §2 Veto Purge · per-card dissolve. When a market transitions
  // to VOIDED_BY_SOVEREIGN we flip its id into `dissolvingIds` so the
  // row can apply `.veto-dissolve`. The CSS animation runs 600ms, so
  // we clear the flag slightly after to let React unmount cleanly on
  // the next store tick (the VOIDED row is filtered out within seconds
  // anyway by `listMintedMarketsForViewer`, but this prevents flicker
  // if the viewer stays on the page).
  const [dissolvingIds, setDissolvingIds] = useState<Record<string, true>>({});
  useEffect(() => subscribeVetoEvents(marketId => {
    setDissolvingIds(prev => ({ ...prev, [marketId]: true }));
    window.setTimeout(() => {
      setDissolvingIds(prev => {
        const next = { ...prev };
        delete next[marketId];
        return next;
      });
    }, 700);
  }), []);

  const viewerId = identity.id ?? '';
  const all = useMemo(() => listMintedMarketsForViewer(viewerId), [viewerId, tick]);
  // Filter by lens too — Queue mirrors the active lens so non-LOCAL
  // lenses show the subset that would promote there on ACTIVE.
  const markets = all.filter(m => m.lens === lens);

  if (markets.length === 0) return null;

  const canVouch = identity.tier >= IdentityLevel.CITIZEN;
  const canVeto  = identity.tier >= IdentityLevel.SOVEREIGN;

  return (
    <div style={{
      backgroundColor: '#0b1220',
      border:          '1px solid #78350f',
      borderRadius:    8,
      padding:         '12px 14px',
      fontFamily:      'monospace',
      marginBottom:    12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <p style={{ color: '#fcd34d', fontSize: 9, letterSpacing: '0.22em', margin: 0, fontWeight: 700 }}>
          ⚒ MINT QUEUE
        </p>
        <span style={{ color: '#78350f', fontSize: 8, letterSpacing: '0.16em' }}>
          §6 · VOUCH / VETO / AUTO-REFUND
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: '#334155', fontSize: 8, letterSpacing: '0.12em' }}>
          {markets.length} PROPOSAL{markets.length === 1 ? '' : 'S'} · {(LENS_OPTIONS.find(o => o.value === lens)?.short ?? lens)}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {markets.map(m => (
          <MintRow
            key={m.id}
            m={m}
            viewerId={viewerId}
            canVouch={canVouch}
            canVeto={canVeto}
            dissolving={!!dissolvingIds[m.id]}
          />
        ))}
      </div>

      <p style={{ color: '#1e293b', fontSize: 8, letterSpacing: '0.14em', margin: '10px 0 0', textAlign: 'center' }}>
        Sandbox lifecycle: 1s ≈ 1h. Pending → Stale at 15h · Veto window 24–72h · Auto-Refund at 72h.
      </p>
    </div>
  );
}

interface RowProps {
  m:          MintedMarket;
  viewerId:   string;
  canVouch:   boolean;
  canVeto:    boolean;
  dissolving: boolean;
}

function MintRow({ m, viewerId, canVouch, canVeto, dissolving }: RowProps): React.JSX.Element {
  const identity = useIdentity();
  const isCreator = m.creatorId === viewerId;
  const vouchCount = m.vouches.size;
  const accent = LENS_ACCENT[m.lens];

  function handleVouch(): void {
    if (!viewerId) return;
    vouchForMarket(m.id, viewerId, identity.tier);
  }

  function handleVeto(): void {
    if (!viewerId) return;
    vetoMarket(m.id, viewerId, identity.tier);
  }

  const border =
    m.status === 'STALE'                ? '#7f1d1d' :
    m.status === 'VOIDED_BY_SOVEREIGN'  ? '#450a0a' :
    m.status === 'AUTO_REFUNDED'        ? '#14532d' :
    m.status === 'ACTIVE'               ? '#14532d' :
                                          '#1e293b';

  return (
    <div
      className={dissolving ? 'veto-dissolve' : undefined}
      style={{
        backgroundColor: '#0f172a',
        border:          `1px solid ${border}`,
        borderRadius:    6,
        padding:         '10px 12px',
        display:         'flex',
        flexDirection:   'column',
        gap:             8,
      }}
    >
      {/* Row header: lens pill · title · seal · status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          color:         accent,
          fontSize:      7,
          letterSpacing: '0.18em',
          fontWeight:    800,
          padding:       '1px 5px',
          border:        `1px solid ${accent}`,
          borderRadius:  3,
        }}>
          {m.lens}
        </span>
        <span style={{ color: '#e2e8f0', fontSize: 11, lineHeight: 1.35, flex: 1 }}>
          {m.title}
        </span>
        {m.fastTrackedBy !== null && <SovereignSeal />}
        <StatusBadge status={m.status} />
      </div>

      {/* Outcome strip — v1.9.1. Compact summary of the multi-way slate;
          The Field, when present, renders distinctly so the catch-all
          reads as a provenance rail rather than a regular outcome. */}
      <OutcomeStrip m={m} />

      {/* Row body varies by status */}
      {m.status === 'PENDING' && (
        <PendingBody
          m={m}
          vouchCount={vouchCount}
          isCreator={isCreator}
          canVouch={canVouch}
          alreadyVouched={viewerId ? m.vouches.has(viewerId) : false}
          onVouch={handleVouch}
        />
      )}
      {m.status === 'ACTIVE' && (
        <ActiveBody m={m} vouchCount={vouchCount} />
      )}
      {m.status === 'STALE' && (
        <StaleBody
          m={m}
          canVeto={canVeto}
          alreadySigned={viewerId ? m.vetoSignatures.includes(viewerId) : false}
          onVeto={handleVeto}
        />
      )}
      {m.status === 'VOIDED_BY_SOVEREIGN' && (
        <TerminalBody
          m={m}
          tone="voided"
          label="BOND + MAKER FEE BURNED · PLEDGERS REFUNDED 1:1"
        />
      )}
      {m.status === 'AUTO_REFUNDED' && (
        <TerminalBody
          m={m}
          tone="refunded"
          label="BOND RETURNED · MAKER FEE FORFEITED · PLEDGERS REFUNDED 1:1"
        />
      )}
    </div>
  );
}

// ─── Outcome strip (v1.9.1) ─────────────────────────────────────────────
//
// One-line summary of a multi-way market's outcomes. Renders the named
// outcomes as muted pills and, when `includeField` is on, a trailing
// "◊ THE FIELD" chip in the lens accent so the catch-all is immediately
// distinguishable. Resolution Hierarchy is also summarised as a tail
// hint so the queue reader sees the adjudication path at a glance.
//
// Binary markets (N = 2, no Field, hierarchy of one) still render here —
// that's fine, a two-pill strip is legible and preserves a uniform row
// shape across the queue.

function OutcomeStrip({ m }: { m: MintedMarket }): React.JSX.Element {
  const namedCount = m.outcomes.filter(o => o.id !== FIELD_OUTCOME_ID).length;
  return (
    <div style={{
      display:        'flex',
      flexWrap:       'wrap',
      alignItems:     'center',
      gap:            6,
      padding:        '6px 0',
      borderTop:      '1px dashed #1e293b',
      borderBottom:   '1px dashed #1e293b',
    }}>
      <span style={{ color: '#475569', fontSize: 7, letterSpacing: '0.22em', fontWeight: 700 }}>
        OUTCOMES
      </span>
      {m.outcomes.map(o => {
        const isField = o.id === FIELD_OUTCOME_ID;
        return (
          <span
            key={o.id}
            title={`${o.label} · ${(o.probability * 100).toFixed(1)}%`}
            style={{
              color:           isField ? '#fcd34d' : '#94a3b8',
              backgroundColor: isField ? 'rgba(252,211,77,0.08)' : '#0b1220',
              border:          `1px solid ${isField ? '#fcd34d' : '#1e293b'}`,
              borderRadius:    3,
              padding:         '1px 6px',
              fontSize:        8,
              letterSpacing:   '0.12em',
              fontWeight:      isField ? 800 : 600,
              maxWidth:        140,
              overflow:        'hidden',
              textOverflow:    'ellipsis',
              whiteSpace:      'nowrap',
            }}
          >
            {isField ? `◊ ${o.label}` : o.label}
          </span>
        );
      })}
      <span style={{ flex: 1 }} />
      <span style={{ color: '#334155', fontSize: 7, letterSpacing: '0.18em' }}>
        {namedCount} NAMED{m.includeField ? ' + FIELD' : ''} · {m.resolutionHierarchy.length} SRC
      </span>
    </div>
  );
}

// ─── Status badge ───────────────────────────────────────────────────────

function StatusBadge({ status }: { status: MintedMarket['status'] }): React.JSX.Element {
  const { label, color, bg } = (() => {
    switch (status) {
      case 'PENDING':             return { label: 'PENDING',   color: '#fcd34d', bg: 'rgba(252,211,77,0.10)' };
      case 'ACTIVE':              return { label: 'ACTIVE',    color: '#86efac', bg: 'rgba(34,197,94,0.12)'  };
      case 'STALE':               return { label: 'STALE',     color: '#fca5a5', bg: 'rgba(239,68,68,0.15)'  };
      case 'VOIDED_BY_SOVEREIGN': return { label: 'VOIDED',    color: '#f87171', bg: 'rgba(127,29,29,0.25)'  };
      case 'AUTO_REFUNDED':       return { label: 'REFUNDED',  color: '#86efac', bg: 'rgba(20,83,45,0.25)'   };
    }
  })();
  return (
    <span style={{
      color,
      backgroundColor: bg,
      fontSize:        7,
      letterSpacing:   '0.22em',
      fontWeight:      800,
      padding:         '2px 7px',
      borderRadius:    3,
      border:          `1px solid ${color}`,
    }}>
      {label}
    </span>
  );
}

// ─── Body variants ──────────────────────────────────────────────────────

function PendingBody({
  m, vouchCount, isCreator, canVouch, alreadyVouched, onVouch,
}: {
  m: MintedMarket; vouchCount: number; isCreator: boolean;
  canVouch: boolean; alreadyVouched: boolean; onVouch: () => void;
}): React.JSX.Element {
  const required = vouchesRequiredFor(m.lens);
  const disabled = isCreator || !canVouch || alreadyVouched;
  const disabledReason =
    isCreator      ? 'CREATOR CANNOT VOUCH' :
    !canVouch      ? 'CITIZEN TIER REQUIRED' :
    alreadyVouched ? 'ALREADY VOUCHED' : '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <VouchMeter count={vouchCount} required={required} lens={m.lens} />
      <span style={{ flex: 1 }} />
      <span style={{ color: '#475569', fontSize: 8, letterSpacing: '0.14em' }}>
        {m.resolutionSource.toUpperCase()} · {m.creationBond} OC BOND
      </span>
      <button
        onClick={onVouch}
        disabled={disabled}
        title={disabled ? disabledReason : 'Vouch for this market'}
        style={{
          padding:         '5px 12px',
          borderRadius:    4,
          border:          `1px solid ${disabled ? '#334155' : '#86efac'}`,
          backgroundColor: disabled ? '#0f172a' : 'rgba(34,197,94,0.14)',
          color:           disabled ? '#334155' : '#86efac',
          fontSize:        9,
          letterSpacing:   '0.18em',
          fontFamily:      'monospace',
          fontWeight:      800,
          cursor:          disabled ? 'not-allowed' : 'pointer',
        }}
      >
        ✓ VOUCH
      </button>
    </div>
  );
}

function VouchMeter({ count, required, lens }: { count: number; required: number; lens: RegionLens }): React.JSX.Element {
  const pct = Math.min(1, count / required) * 100;
  // On GLOBAL, a single Sovereign vouch is an alternative path per §6.
  // The suffix makes the tiered rule legible inside the row itself.
  const suffix = lens === 'GLOBAL' ? ' OR 1 SOVEREIGN' : '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ color: '#94a3b8', fontSize: 9, letterSpacing: '0.14em', fontWeight: 700 }}>
        {count}/{required} CITIZEN{suffix}
      </span>
      <div style={{ width: 70, height: 4, borderRadius: 2, background: '#1e293b', overflow: 'hidden' }}>
        <div style={{
          width:           `${pct}%`,
          height:          '100%',
          background:      count >= required ? '#22c55e' : '#fcd34d',
          transition:      'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

// Sovereign Seal — marks a market that was fast-tracked (self-mint or
// inbound bypass) per §6. Gold, small, sits between the title and the
// status badge so it reads as a provenance stamp rather than a state.
function SovereignSeal(): React.JSX.Element {
  return (
    <span
      title="Sovereign Seal — fast-tracked per §6. Tier-3 endorsement."
      style={{
        display:         'inline-flex',
        alignItems:      'center',
        gap:             3,
        color:           '#fcd34d',
        fontSize:        7,
        letterSpacing:   '0.22em',
        fontWeight:      800,
        padding:         '2px 6px',
        borderRadius:    3,
        border:          '1px solid #fcd34d',
        backgroundColor: 'rgba(252,211,77,0.10)',
        textShadow:      '0 0 4px rgba(252,211,77,0.35)',
        whiteSpace:      'nowrap',
      }}
    >
      ✦ SOVEREIGN SEAL
    </span>
  );
}

function ActiveBody({ m, vouchCount }: { m: MintedMarket; vouchCount: number }): React.JSX.Element {
  const hoursUntilScheduled = Math.max(0, Math.round((m.scheduledResolutionAt - Date.now()) / 1000));
  const promotionLabel = m.fastTrackedBy !== null
    ? '✦ FAST-TRACKED · SOVEREIGN SEAL'
    : `✓ PROMOTED · ${vouchCount} VOUCHES`;
  const promotionColor = m.fastTrackedBy !== null ? '#fcd34d' : '#86efac';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ color: promotionColor, fontSize: 9, letterSpacing: '0.14em', fontWeight: 700 }}>
        {promotionLabel}
      </span>
      <span style={{ flex: 1 }} />
      <span style={{ color: '#475569', fontSize: 8, letterSpacing: '0.14em' }}>
        MAKER FEE ACCRUED · {m.makerFeeAccrued.toFixed(2)} OC
      </span>
      <span style={{ color: '#94a3b8', fontSize: 8, letterSpacing: '0.14em' }}>
        RESOLVES IN {hoursUntilScheduled}h
      </span>
    </div>
  );
}

function StaleBody({
  m, canVeto, alreadySigned, onVeto,
}: {
  m: MintedMarket; canVeto: boolean; alreadySigned: boolean; onVeto: () => void;
}): React.JSX.Element {
  const phase = vetoPhaseFor(m);
  const now   = Date.now();

  const vetoSecondsLeft = phase.autoRefundAt ? Math.max(0, Math.round((phase.autoRefundAt - now) / 1000)) : 0;
  const vetoOpensInSec  = phase.opensAt      ? Math.max(0, Math.round((phase.opensAt      - now) / 1000)) : 0;

  // Disable the Veto button outside the window. Sandbox quorum is a
  // single Sovereign signature; the label makes that explicit so the
  // tester doesn't expect a 3-click flow.
  const buttonDisabled =
    !canVeto || alreadySigned || phase.phase !== 'VETO_OPEN';

  const buttonHint =
    !canVeto                       ? 'SOVEREIGN TIER REQUIRED' :
    alreadySigned                  ? 'ALREADY SIGNED' :
    phase.phase === 'PRE_VETO'     ? `VETO WINDOW OPENS IN ${vetoOpensInSec}s` :
    phase.phase === 'VETO_OPEN'    ? 'SIGN VETO — 3-SOVEREIGN QUORUM (SANDBOX-SIMULATED)' :
                                     'VETO WINDOW CLOSED';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: '#fca5a5', fontSize: 9, letterSpacing: '0.14em', fontWeight: 700 }}>
          ⚠ STALE · {m.resolutionSource.toUpperCase()} RETURNED NO SIGNAL
        </span>
        <span style={{ flex: 1 }} />
        {phase.phase === 'PRE_VETO' && (
          <span style={{ color: '#fcd34d', fontSize: 8, letterSpacing: '0.14em', fontWeight: 700 }}>
            VETO WINDOW IN {vetoOpensInSec}s
          </span>
        )}
        {phase.phase === 'VETO_OPEN' && (
          <span style={{ color: '#fcd34d', fontSize: 8, letterSpacing: '0.14em', fontWeight: 700 }}>
            AUTO-REFUND IN {vetoSecondsLeft}s
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 3, borderRadius: 2, background: '#1e293b', overflow: 'hidden' }}>
          {/* Progress bar from STALE → AUTO_REFUND. Fills left-to-right. */}
          <StaleProgress m={m} />
        </div>
        <button
          onClick={onVeto}
          disabled={buttonDisabled}
          title={buttonHint}
          style={{
            padding:         '5px 12px',
            borderRadius:    4,
            border:          `1px solid ${buttonDisabled ? '#334155' : '#dc2626'}`,
            backgroundColor: buttonDisabled ? '#0f172a' : 'rgba(220,38,38,0.18)',
            color:           buttonDisabled ? '#334155' : '#fca5a5',
            fontSize:        9,
            letterSpacing:   '0.18em',
            fontFamily:      'monospace',
            fontWeight:      800,
            cursor:          buttonDisabled ? 'not-allowed' : 'pointer',
            whiteSpace:      'nowrap',
          }}
        >
          ⚔ SOVEREIGN VETO
        </button>
      </div>
      <p style={{ color: '#334155', fontSize: 8, letterSpacing: '0.14em', margin: 0 }}>
        {phase.phase === 'VETO_OPEN'
          ? '3-Sovereign quorum Veto voids the market and burns bond + accrued Maker Fee. Otherwise the bond auto-refunds at T+72h.'
          : phase.phase === 'PRE_VETO'
            ? 'Pledges frozen. 3-Sovereign Veto window opens at T+24h; Auto-Refund fires at T+72h.'
            : 'Veto window closed. Auto-Refund imminent.'}
      </p>
    </div>
  );
}

function StaleProgress({ m }: { m: MintedMarket }): React.JSX.Element {
  if (m.staleAt === null) return <div style={{ width: 0, height: '100%' }} />;
  const total   = 72 * 1000; // 72 sim-hours ≈ 72 real seconds
  const elapsed = Math.min(total, Math.max(0, Date.now() - m.staleAt));
  const pct     = (elapsed / total) * 100;
  // Colour crosses from amber (pre-veto) → red (veto-open) → emerald (auto-refund zone).
  const col = elapsed < 24_000 ? '#f59e0b'
            : elapsed < 72_000 ? '#ef4444'
            :                    '#22c55e';
  return (
    <div style={{
      width:           `${pct}%`,
      height:          '100%',
      backgroundColor: col,
      transition:      'width 0.6s linear',
    }} />
  );
}

function TerminalBody({
  m, tone, label,
}: {
  m: MintedMarket; tone: 'voided' | 'refunded'; label: string;
}): React.JSX.Element {
  const col = tone === 'voided' ? '#f87171' : '#86efac';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ color: col, fontSize: 8, letterSpacing: '0.18em', fontWeight: 700 }}>
        {label}
      </span>
      <span style={{ flex: 1 }} />
      <span style={{ color: '#475569', fontSize: 8, letterSpacing: '0.14em' }}>
        BOND {m.creationBond} OC · FEE {m.makerFeeAccrued.toFixed(2)} OC
      </span>
    </div>
  );
}
